import { useMemo, useState, useCallback, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { DocTypeListPageShell } from "@/features/common/components/DocTypeListPageShell";
import { LIST_PAGE_SHELL } from "@/components/layout/pageShell";
import { useStockReconciliationListWithPagination } from "./api/stockReconciliationHooks";
import { createStockReconciliationColumns } from "./stockReconciliationColumns";
import type { StockReconciliation } from "@/lib/erp";
import { buildNewRoute } from "@/lib/config/docRoutes";
import { injectActionsColumn } from "@/features/list/actionsColumn";
import { GenericInlineDetailPanel } from "@/features/list/GenericInlineDetailPanel";
import { GenericDetailPanel } from "@/features/list/GenericDetailPanel";
import { useDetailPanelState } from "@/features/list/useDetailPanelState";
import { ClipboardCheck } from "lucide-react";
import type { SubRowColumnInfo } from "@/features/common/components/ListTableBody";

export function StockReconciliationListPage() {
  const navigate = useNavigate();
  const [inlineDetailDocName, setInlineDetailDocName] = useState<string | null>(null);
  const handleInlineClose = useCallback(() => setInlineDetailDocName(null), []);
  const { detailPanelItem, panelItem, openDetailPanel, closeDetailPanel } = useDetailPanelState();

  const renderSubRow = useCallback(
    (row: StockReconciliation, columnInfo: SubRowColumnInfo): ReactNode | null => {
      if (!row.name || row.name !== inlineDetailDocName) return null;
      return (
        <GenericInlineDetailPanel
          key={row.name}
          doctype="Stock Reconciliation"
          docName={row.name}
          rowData={row as unknown as Record<string, unknown>}
          onClose={handleInlineClose}
          columns={columnInfo}
        />
      );
    },
    [inlineDetailDocName, handleInlineClose],
  );

  const buildColumns = useMemo(
    () => ({
      items: _items,
      visibleFieldOrder,
      availableFields
    }: {
      items: StockReconciliation[];
      visibleFieldOrder?: string[];
      availableFields?: Array<{ fieldname: string; label: string; fieldtype?: string }>;
    }) => {
      const baseCols = createStockReconciliationColumns({ visibleFieldOrder, availableFields });
      return injectActionsColumn(baseCols, {
        onToggleDetail: (name) => setInlineDetailDocName((prev) => prev === name ? null : name),
        onOpen: (name) => openDetailPanel(name),
      });
    },
    [navigate]
  );

  return (
    <div className={LIST_PAGE_SHELL}>
      <div>
        <DocTypeListPageShell<StockReconciliation>
          doctype="Stock Reconciliation"
          columns={buildColumns}
          filterStoreKey="stock.stock_reconciliation"
          useListHook={useStockReconciliationListWithPagination}
          defaultSorting={[{ id: "posting_date", desc: true }]}
          searchPlaceholder="Search by name..."
          title="Stock Reconciliations"
          newButtonLabel="New Stock Reconciliation"
          onNewClick={() => navigate(buildNewRoute("Stock Reconciliation"))}
          onRowClick={undefined}
          renderSubRow={renderSubRow}
          activeSubRowId={inlineDetailDocName ?? undefined}
          getRowId={(row) => row.name}
          inlineEditable
          isInlineEditMode
          compactMode
          noPadding
          fullWidth
          className="pt-0"
          tableContainerClassName="overflow-x-auto overflow-y-hidden border-y border-border bg-white dark:bg-neutral-950"
          breadcrumbs={[
            {
              label: "Stock Reconciliations",
              icon: (
                <span className="flex items-center justify-center h-5 w-5 rounded-xs bg-teal-500 shrink-0">
                  <ClipboardCheck className="h-3 w-3 text-white" />
                </span>
              ),
            },
          ]}
          enableViewModeSwitch={true}
          searchConfig={{
            showInitialSuggestions: false,
            minSearchLength: 3,
          }}
          listSettingsConfig={{
            key: "stock.stock_reconciliation",
            meta: {
              doctype: "Stock Reconciliation",
              includeFieldtypes: [
                "Data",
                "Link",
                "Select",
                "Date",
                "Time",
                "Datetime",
                "Currency",
                "Float",
                "Int",
                "Check",
                "Text",
              ],
              excludeFieldtypes: [
                "Section Break",
                "Column Break",
                "Tab Break",
                "Table",
                "HTML",
                "Button",
                "Image",
                "Attach",
                "Attach Image",
                "Barcode",
                "Code",
                "JSON",
                "Long Text",
                "Markdown Editor",
                "Password",
                "Read Only",
                "Small Text",
              ],
              excludeFieldnames: [
                "naming_series",
                "amended_from",
                "scan_barcode",
                "scan_mode",
                "last_scanned_warehouse",
                "set_posting_time",
                "items",
              ],
              respectInListView: true,
              requiredFieldnames: ["name"],
            },
          }}
        />
      </div>

      <GenericDetailPanel
        open={!!detailPanelItem}
        onClose={closeDetailPanel}
        doctype="Stock Reconciliation"
        docName={panelItem?.name ?? ""}
        docLabel={panelItem?.label}
      />
    </div>
  );
}
